import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getQuotation, createQuotation, getClients, getServices } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, Plus, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';

export default function QuotationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [quotation, setQuotation] = useState({
    client_id: '',
    validity_days: 30,
    items: [{ description: '', quantity: 1, unit_price: 0, govt_fee: 0, service_fee: 0, vat_applicable: true }],
    discount_type: 'None',
    discount_value: 0,
    vat_percentage: 5,
    notes: '',
    terms_conditions: ''
  });
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [clientsRes, servicesRes] = await Promise.all([
        getClients({ limit: 1000 }),
        getServices({ limit: 200 })
      ]);
      setClients(clientsRes.data.items);
      setServices(servicesRes.data.items);

      if (!isNew) {
        const res = await getQuotation(id);
        setQuotation(res.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setQuotation((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setQuotation((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setQuotation((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0, govt_fee: 0, service_fee: 0, vat_applicable: true }]
    }));
  };

  const addServiceAsItem = (serviceId) => {
    const svc = services.find(s => s.id === serviceId);
    if (svc) {
      setQuotation((prev) => ({
        ...prev,
        items: [...prev.items, {
          description: svc.name,
          quantity: 1,
          unit_price: 0,
          govt_fee: svc.govt_fee || 0,
          service_fee: svc.service_fee_default || 0,
          vat_applicable: true,
          service_id: serviceId
        }]
      }));
    }
  };

  const removeItem = (index) => {
    setQuotation((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = quotation.items.reduce((sum, item) => {
      return sum + (item.quantity * item.unit_price) + (item.govt_fee || 0) + (item.service_fee || 0);
    }, 0);
    
    let discount = 0;
    if (quotation.discount_type === 'Percentage') {
      discount = subtotal * (quotation.discount_value / 100);
    } else if (quotation.discount_type === 'Fixed') {
      discount = quotation.discount_value;
    }
    
    const taxable = subtotal - discount;
    const vat = taxable * (quotation.vat_percentage / 100);
    const total = taxable + vat;
    
    return { subtotal, discount, taxable, vat, total };
  };

  const handleSave = async () => {
    if (!quotation.client_id || quotation.items.length === 0) {
      toast.error('Please select client and add items');
      return;
    }

    setSaving(true);
    try {
      const res = await createQuotation(quotation);
      toast.success('Quotation created');
      navigate(`/quotations/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const totals = calculateTotals();

  // Detail view for existing quotation
  if (!isNew) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/quotations')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                <h1 className="text-2xl font-bold">{quotation.quote_number}</h1>
                <Badge>{quotation.status}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">Created {formatDate(quotation.quote_date)}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Quotation Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                  <div><span className="text-muted-foreground">Client:</span> <span className="font-medium">{quotation.client_name}</span></div>
                  <div><span className="text-muted-foreground">Valid Until:</span> <span className="font-medium">{formatDate(quotation.expiry_date)}</span></div>
                </div>
                
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Description</th>
                      <th className="text-right py-2">Qty</th>
                      <th className="text-right py-2">Govt Fee</th>
                      <th className="text-right py-2">Service Fee</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotation.items?.map((item, idx) => (
                      <tr key={idx} className="border-b border-border/50">
                        <td className="py-2">{item.description}</td>
                        <td className="py-2 text-right">{item.quantity}</td>
                        <td className="py-2 text-right font-mono">{formatCurrency(item.govt_fee)}</td>
                        <td className="py-2 text-right font-mono">{formatCurrency(item.service_fee)}</td>
                        <td className="py-2 text-right font-mono">{formatCurrency(item.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">{formatCurrency(quotation.subtotal)}</span>
                </div>
                {quotation.discount_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="font-mono text-red-500">-{formatCurrency(quotation.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">VAT ({quotation.vat_percentage}%)</span>
                  <span className="font-mono">{formatCurrency(quotation.vat_amount)}</span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-mono font-semibold text-lg">{formatCurrency(quotation.total_amount)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // New quotation form
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/quotations')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <h1 className="text-2xl font-bold">New Quotation</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Client & Validity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client *</Label>
                  <Select value={quotation.client_id} onValueChange={(v) => handleChange('client_id', v)}>
                    <SelectTrigger data-testid="quote-client">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valid for (days)</Label>
                  <Input
                    type="number"
                    value={quotation.validity_days}
                    onChange={(e) => handleChange('validity_days', parseInt(e.target.value) || 30)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Line Items</CardTitle>
              <div className="flex gap-2">
                <Select onValueChange={addServiceAsItem}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Add from services" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Description</th>
                    <th className="text-right py-2 w-20">Qty</th>
                    <th className="text-right py-2 w-28">Govt Fee</th>
                    <th className="text-right py-2 w-28">Service Fee</th>
                    <th className="text-right py-2 w-28">Total</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {quotation.items.map((item, idx) => {
                    const lineTotal = (item.quantity * item.unit_price) + (item.govt_fee || 0) + (item.service_fee || 0);
                    return (
                      <tr key={idx} className="border-b border-border/50">
                        <td className="py-2">
                          <Input
                            value={item.description}
                            onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                            placeholder="Description"
                          />
                        </td>
                        <td className="py-2">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(idx, 'quantity', parseFloat(e.target.value) || 1)}
                            className="text-right"
                          />
                        </td>
                        <td className="py-2">
                          <Input
                            type="number"
                            value={item.govt_fee}
                            onChange={(e) => handleItemChange(idx, 'govt_fee', parseFloat(e.target.value) || 0)}
                            className="text-right"
                          />
                        </td>
                        <td className="py-2">
                          <Input
                            type="number"
                            value={item.service_fee}
                            onChange={(e) => handleItemChange(idx, 'service_fee', parseFloat(e.target.value) || 0)}
                            className="text-right"
                          />
                        </td>
                        <td className="py-2 text-right font-mono">{formatCurrency(lineTotal)}</td>
                        <td className="py-2">
                          <Button variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Notes to Client</Label>
                <Textarea
                  value={quotation.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={2}
                />
              </div>
              <div>
                <Label>Terms & Conditions</Label>
                <Textarea
                  value={quotation.terms_conditions || ''}
                  onChange={(e) => handleChange('terms_conditions', e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Select value={quotation.discount_type} onValueChange={(v) => handleChange('discount_type', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="None">No Discount</SelectItem>
                      <SelectItem value="Percentage">Percentage</SelectItem>
                      <SelectItem value="Fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                  {quotation.discount_type !== 'None' && (
                    <Input
                      type="number"
                      value={quotation.discount_value}
                      onChange={(e) => handleChange('discount_value', parseFloat(e.target.value) || 0)}
                    />
                  )}
                </div>
                
                {totals.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="font-mono text-red-500">-{formatCurrency(totals.discount)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">VAT ({quotation.vat_percentage}%)</span>
                  <span className="font-mono">{formatCurrency(totals.vat)}</span>
                </div>
                
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-mono font-semibold text-lg">{formatCurrency(totals.total)}</span>
                </div>
              </div>

              <Button className="w-full" onClick={handleSave} disabled={saving} data-testid="save-quotation-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Create Quotation'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
